/**
 * Llamadas HTTP del dominio Equipo (usuarios + roles + catálogo de permisos).
 *
 * SIEMPRE sobre el cliente único `lib/api` (`docs/frontend_architecture.md`,
 * Pr. 2): ningún `fetch`/`ky` suelto. El cliente aporta `credentials: 'include'`
 * y el `X-CSRF-TOKEN`. La gestión de usuarios/roles es una operación GLOBAL de
 * administración (no acotada por sede), igual que el SSR: el backend no exige
 * `X-Sede-Id` aquí, así que estas llamadas no dependen de la sede activa.
 *
 * Un `ApiError` (401/403/404/4xx/5xx) lo traduce el cliente y lo gestionan los
 * hooks/pantallas (`team/format.ts`).
 *
 * Cubre la superficie de `application/api/domains/users.py` y `roles.py` (#53/#54).
 */
import { api } from "@/lib/api";

import type {
  ChangePasswordBody,
  CreateRoleBody,
  CreateUserBody,
  PermissionsCatalog,
  Role,
  RolesList,
  SetPermissionsBody,
  UpdateRoleBody,
  UpdateUserBody,
  UserDetail,
  UsersGrouped,
} from "./types";

/** Codifica un username/role_id para usarlo como segmento de ruta. */
function encodeSegment(value: string): string {
  return encodeURIComponent(value);
}

// --------------------------------------------------------------------------
// Usuarios
// --------------------------------------------------------------------------

/**
 * Lista los usuarios agrupados por sede (`GET /users?group_by=sede`).
 *
 * Permiso backend: `manage_users`. Réplica del acordeón por sede del SSR.
 */
export async function fetchUsersGrouped(): Promise<UsersGrouped> {
  const response = await api.get("users", {
    searchParams: { group_by: "sede" },
  });
  return (await response.json()) as UsersGrouped;
}

/**
 * Detalle de un usuario (`GET /users/<username>`).
 *
 * Permiso backend: `manage_users`. Vista `detail`: permisos efectivos del
 * override (`permissions`, `[]` si usa los del rol) + timestamps.
 */
export async function fetchUser(username: string): Promise<UserDetail> {
  const response = await api.get(`users/${encodeSegment(username)}`);
  return (await response.json()) as UserDetail;
}

/**
 * Crea un usuario (`POST /users`).
 *
 * Permiso backend: `manage_users`. La validación (obligatorios, unicidad, rol y
 * sede válidos, longitud de contraseña) la hace el backend; devuelve 201 con la
 * vista `detail`.
 */
export async function createUser(body: CreateUserBody): Promise<UserDetail> {
  const response = await api.post("users", { json: body });
  return (await response.json()) as UserDetail;
}

/**
 * Edita un usuario (`PATCH /users/<username>`).
 *
 * Permiso backend: `manage_users`. PATCH parcial: solo viajan los campos a
 * cambiar. `password` solo se envía si se quiere cambiar. Devuelve la vista
 * `detail`.
 */
export async function updateUser(
  username: string,
  body: UpdateUserBody,
): Promise<UserDetail> {
  const response = await api.patch(`users/${encodeSegment(username)}`, {
    json: body,
  });
  return (await response.json()) as UserDetail;
}

/**
 * Cambia la contraseña de un usuario (`POST /users/<username>/password`).
 *
 * Permiso backend: `manage_users`. La contraseña debe tener al menos 6
 * caracteres (lo valida el backend; la UI lo valida también con Zod).
 */
export async function changeUserPassword(
  username: string,
  body: ChangePasswordBody,
): Promise<UserDetail> {
  const response = await api.post(`users/${encodeSegment(username)}/password`, {
    json: body,
  });
  return (await response.json()) as UserDetail;
}

/**
 * Fija los permisos personalizados (override) de un usuario
 * (`PATCH /users/<username>/permissions`).
 *
 * Permiso backend: `manage_users`. Sustituye el override actual (no lo combina).
 * Devuelve la vista `detail`.
 */
export async function setUserPermissions(
  username: string,
  body: SetPermissionsBody,
): Promise<UserDetail> {
  const response = await api.patch(
    `users/${encodeSegment(username)}/permissions`,
    { json: body },
  );
  return (await response.json()) as UserDetail;
}

/**
 * Resetea los permisos de un usuario a los de su rol
 * (`POST /users/<username>/permissions/reset`).
 *
 * Permiso backend: `manage_users`. Quita el override (`permissions = null`).
 */
export async function resetUserPermissions(username: string): Promise<UserDetail> {
  const response = await api.post(
    `users/${encodeSegment(username)}/permissions/reset`,
  );
  return (await response.json()) as UserDetail;
}

/**
 * Elimina un usuario (`DELETE /users/<username>`).
 *
 * Permiso backend: `manage_users`. Protecciones: no el último admin
 * (`last_admin`) ni el propio actor (`self_delete`). 204 sin cuerpo.
 */
export async function deleteUser(username: string): Promise<void> {
  await api.delete(`users/${encodeSegment(username)}`);
}

// --------------------------------------------------------------------------
// Roles
// --------------------------------------------------------------------------

/**
 * Lista los roles del sistema y los personalizados (`GET /roles`).
 *
 * Permiso backend: `manage_roles`. Devuelve `{system, custom}` con la vista
 * `list` de cada rol (permisos incluidos para el badge).
 */
export async function fetchRoles(): Promise<RolesList> {
  const response = await api.get("roles");
  return (await response.json()) as RolesList;
}

/**
 * Detalle de un rol (`GET /roles/<role_id>`).
 *
 * Permiso backend: `manage_roles`. Vista `detail` (permisos + timestamps).
 */
export async function fetchRole(roleId: string): Promise<Role> {
  const response = await api.get(`roles/${encodeSegment(roleId)}`);
  return (await response.json()) as Role;
}

/**
 * Crea un rol personalizado (`POST /roles`).
 *
 * Permiso backend: `manage_roles`. El `role_id` debe casar `[a-z0-9_]` y ser
 * único (lo valida el backend; la UI lo valida con Zod). Devuelve 201 con la
 * vista `detail`.
 */
export async function createRole(body: CreateRoleBody): Promise<Role> {
  const response = await api.post("roles", { json: body });
  return (await response.json()) as Role;
}

/**
 * Edita un rol (`PATCH /roles/<role_id>`).
 *
 * Permiso backend: `manage_roles`. Permite editar roles del sistema y
 * personalizados (igual que el SSR). Devuelve la vista `detail`.
 */
export async function updateRole(
  roleId: string,
  body: UpdateRoleBody,
): Promise<Role> {
  const response = await api.patch(`roles/${encodeSegment(roleId)}`, {
    json: body,
  });
  return (await response.json()) as Role;
}

/**
 * Elimina un rol personalizado (`DELETE /roles/<role_id>`).
 *
 * Permiso backend: `manage_roles`. Protecciones: no roles del sistema
 * (`is_system_role`) ni roles en uso (`role_in_use`). 204 sin cuerpo.
 */
export async function deleteRole(roleId: string): Promise<void> {
  await api.delete(`roles/${encodeSegment(roleId)}`);
}

// --------------------------------------------------------------------------
// Catálogo de permisos
// --------------------------------------------------------------------------

/**
 * Catálogo de permisos disponibles con sus descripciones (`GET /permissions`).
 *
 * Permiso backend: `manage_users`. Soporte de los formularios de permisos
 * granulares y de roles. Lista plana `{items: [{id, description}]}`.
 */
export async function fetchPermissions(): Promise<PermissionsCatalog> {
  const response = await api.get("permissions");
  return (await response.json()) as PermissionsCatalog;
}
